// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    makeStyles,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarGroup,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { removeAlert } from '../../redux/features/app/appSlice';
import { Dismiss16 } from './BundledIcons';
import { AlertType } from '../../libs/models/AlertType';

const useClasses = makeStyles({
    alert: {
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground6,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },
    actionItems: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(tokens.spacingHorizontalMNudge),
    },
    button: {
        alignSelf: 'center',
    },
});

export const Alerts: React.FC = () => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { alerts } = useAppSelector((state: RootState) => state.app);

    return (
        <MessageBarGroup>
            {alerts.map(({ type, message, onRetry }, index) => {
                let dismissTimeout: NodeJS.Timeout | undefined;
                if (type == AlertType.Success) {
                    dismissTimeout = setTimeout(() => {
                        dispatch(removeAlert(index));
                    }, 3000);
                }

                return (
                    <MessageBar layout="multiline" intent={type} key={`${index}-${type}`} className={classes.alert}>
                        <MessageBarBody>{message.slice(0, 1000) + (message.length > 1000 ? '...' : '')}</MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss message"
                                    appearance="transparent"
                                    icon={<Dismiss16 />}
                                    onClick={() => {
                                        clearTimeout(dismissTimeout);
                                        dispatch(removeAlert(index));
                                    }}
                                />
                            }
                        >
                            {onRetry && <Button onClick={onRetry}>Retry</Button>}
                        </MessageBarActions>
                    </MessageBar>
                );
            })}
        </MessageBarGroup>
    );
};
